import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import { useAuth } from '../../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const RefreshWabaTokenDialog = ({ open, onClose, environment, wabaConnection }) => {
  const { refreshWabaToken } = useEnvironment();
  const { authenticatedFetch } = useAuth();

  const [step, setStep] = useState('initial'); // 'initial', 'authenticating', 'refreshing', 'success', 'error'
  const [error, setError] = useState(null);

  const handleStartRefresh = async () => {
    setStep('authenticating');
    setError(null);

    if (!window.FB) {
      setError('Facebook SDK not loaded. Please refresh the page and try again.');
      setStep('error');
      return;
    }

    window.FB.login(
      (response) => {
        // Use IIFE to handle async logic inside the sync callback
        (async () => {
          if (response.status === 'connected') {
            try {
              var authResponse = response.authResponse;
              var token = authResponse.accessToken;
              var expiresIn = authResponse.expiresIn;

              // Verify the token is for the same WABA by discovering WABAs
              setStep('refreshing');

              var discoveryResponse = await authenticatedFetch(`${API_URL}/api/whatsapp/discover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: token })
              });

              if (!discoveryResponse.ok) {
                throw new Error('Failed to verify WhatsApp Business Account access');
              }

              var discoveryData = await discoveryResponse.json();

              // Check if the existing WABA is in the discovered list
              var matchingWaba = discoveryData.wabas.find(
                waba => waba.id === wabaConnection.wabaId
              );

              if (!matchingWaba) {
                throw new Error(
                  `The authenticated Facebook account does not have access to the WABA "${wabaConnection.wabaName || wabaConnection.wabaId}". ` +
                  'Please log in with the correct Facebook account that has access to this WhatsApp Business Account.'
                );
              }

              if (!matchingWaba.isValid) {
                throw new Error(
                  `The WABA "${wabaConnection.wabaName || wabaConnection.wabaId}" could not be verified. ` +
                  'Please ensure the account has the required permissions.'
                );
              }

              // Token is valid for the same WABA - refresh it
              await refreshWabaToken(environment.id, token, expiresIn);

              setStep('success');

              // Close dialog after a short delay
              setTimeout(() => {
                handleClose();
              }, 1500);
            } catch (err) {
              console.error('Error refreshing token:', err);
              setError(err.message || 'Failed to refresh token');
              setStep('error');
            }
          } else {
            setError('Facebook login failed or was cancelled');
            setStep('error');
          }
        })();
      },
      {
        scope: 'whatsapp_business_management,business_management,whatsapp_business_manage_events',
        auth_type: 'rerequest'
      }
    );
  };

  const handleClose = () => {
    if (step !== 'authenticating' && step !== 'refreshing') {
      setStep('initial');
      setError(null);
      onClose();
    }
  };

  const handleRetry = () => {
    setStep('initial');
    setError(null);
  };

  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!wabaConnection?.tokenExpiresAt) {
      return null;
    }
    const expiresAt = new Date(wabaConnection.tokenExpiresAt);
    const now = new Date();
    const diffTime = expiresAt - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();

  const renderContent = () => {
    switch (step) {
      case 'initial':
        return (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Refreshing the token will extend access to the same WhatsApp Business Account.
                You cannot change the connected WABA during a token refresh.
              </Typography>
            </Alert>

            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Connected WhatsApp Business Account
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {wabaConnection?.wabaName || wabaConnection?.wabaId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {wabaConnection?.wabaId}
              </Typography>

              {daysRemaining !== null && (
                <Box sx={{ mt: 1.5 }}>
                  <Chip
                    size="small"
                    icon={daysRemaining <= 7 ? <WarningIcon /> : undefined}
                    label={
                      daysRemaining <= 0
                        ? 'Token expired'
                        : daysRemaining === 1
                        ? '1 day remaining'
                        : `${daysRemaining} days remaining`
                    }
                    color={
                      daysRemaining <= 0
                        ? 'error'
                        : daysRemaining <= 7
                        ? 'warning'
                        : 'success'
                    }
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary">
              You'll be redirected to Facebook to re-authenticate. Make sure to log in with the same
              account that has access to the WhatsApp Business Account shown above.
            </Typography>
          </>
        );

      case 'authenticating':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Authenticating with Facebook...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please complete the login if prompted
            </Typography>
          </Box>
        );

      case 'refreshing':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1">
              Verifying and refreshing token...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This may take a few seconds
            </Typography>
          </Box>
        );

      case 'success':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Token Refreshed Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your token has been extended for another 60 days.
            </Typography>
          </Box>
        );

      case 'error':
        return (
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please try again. Make sure you're logging in with the correct Facebook account
              that has access to the WhatsApp Business Account.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const renderActions = () => {
    switch (step) {
      case 'initial':
        return (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleStartRefresh}
              variant="contained"
              startIcon={<RefreshIcon />}
            >
              Refresh with Facebook
            </Button>
          </>
        );

      case 'authenticating':
      case 'refreshing':
        return (
          <Button disabled>
            Please wait...
          </Button>
        );

      case 'success':
        return null;

      case 'error':
        return (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleRetry} variant="contained">
              Try Again
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step === 'authenticating' || step === 'refreshing'}
    >
      <DialogTitle>Refresh WhatsApp Token</DialogTitle>

      <DialogContent>
        {renderContent()}
      </DialogContent>

      <DialogActions>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
};

export default RefreshWabaTokenDialog;
